<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import ChatAPI from 'custom/api/chat';
import { useLocoI18n } from 'custom/composables/useLocoI18n';

// Enter sends, Shift+Enter adds a line. "@" opens the people picker. Top-level drafts are saved on the server.
const props = defineProps({
  channelId: { type: Number, required: true },
  parentId: { type: Number, default: null },
  placeholder: { type: String, default: '' },
  people: { type: Array, default: () => [] },
  initialDraft: { type: String, default: '' },
});

const emit = defineEmits(['sent']);

const { t } = useLocoI18n();

const TYPING_EVERY_MS = 3000;
const DRAFT_DELAY_MS = 1500;

const text = ref(props.initialDraft || '');
const files = ref([]);
const mentions = ref(new Set());
const isSending = ref(false);
const textareaRef = ref(null);
const fileInputRef = ref(null);
const mentionQuery = ref(null);
const mentionIndex = ref(0);
let lastTypingAt = 0;
let draftTimer = null;

const suggestions = computed(() => {
  if (mentionQuery.value === null) return [];
  const query = mentionQuery.value.toLowerCase();
  return props.people
    .filter(person => person.name.toLowerCase().includes(query))
    .slice(0, 6);
});

const canSend = computed(
  () => (text.value.trim() || files.value.length) && !isSending.value
);

const resize = () => {
  const element = textareaRef.value;
  if (!element) return;
  element.style.height = 'auto';
  element.style.height = `${Math.min(element.scrollHeight, 200)}px`;
};

// "@ana" right before the cursor
const updateMentionQuery = () => {
  const element = textareaRef.value;
  const before = text.value.slice(0, element.selectionStart);
  const match = before.match(/(^|\s)@([^\s@]{0,30})$/);
  mentionQuery.value = match ? match[2] : null;
  mentionIndex.value = 0;
};

const pickMention = person => {
  const element = textareaRef.value;
  const cursor = element.selectionStart;
  const before = text.value
    .slice(0, cursor)
    .replace(/@([^\s@]*)$/, `@${person.name} `);
  text.value = before + text.value.slice(cursor);
  mentions.value.add(person.id);
  mentionQuery.value = null;
  nextTick(() => {
    element.focus();
    element.setSelectionRange(before.length, before.length);
  });
};

const pingTyping = () => {
  const now = Date.now();
  if (now - lastTypingAt < TYPING_EVERY_MS) return;
  lastTypingAt = now;
  ChatAPI.typing(props.channelId).catch(() => {});
};

const scheduleDraft = () => {
  if (props.parentId) return;
  clearTimeout(draftTimer);
  draftTimer = setTimeout(() => {
    ChatAPI.saveDraft(props.channelId, text.value).catch(() => {});
  }, DRAFT_DELAY_MS);
};

const onInput = () => {
  resize();
  updateMentionQuery();
  pingTyping();
  scheduleDraft();
};

const send = async () => {
  if (!canSend.value) return;
  isSending.value = true;
  try {
    // Only people whose @name is still in the text count as mentioned
    const mentioned = props.people
      .filter(person => mentions.value.has(person.id))
      .filter(person => text.value.includes(`@${person.name}`))
      .map(person => person.id);
    const { data } = await ChatAPI.send(props.channelId, {
      content: text.value.trim(),
      mentions: mentioned,
      parentId: props.parentId,
      files: files.value,
    });
    text.value = '';
    files.value = [];
    mentions.value = new Set();
    clearTimeout(draftTimer);
    nextTick(resize);
    emit('sent', data);
  } catch (error) {
    useAlert(
      t('LOCO.CHAT.ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  } finally {
    isSending.value = false;
  }
};

const onKeydown = event => {
  if (suggestions.value.length) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      mentionIndex.value =
        (mentionIndex.value + step + suggestions.value.length) %
        suggestions.value.length;
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      pickMention(suggestions.value[mentionIndex.value]);
      return;
    }
    if (event.key === 'Escape') {
      mentionQuery.value = null;
      return;
    }
  }
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    send();
  }
};

const onFiles = event => {
  files.value = [...files.value, ...Array.from(event.target.files || [])];
  event.target.value = '';
};

const removeFile = index => {
  files.value = files.value.filter((_, position) => position !== index);
};

watch(
  () => props.initialDraft,
  value => {
    text.value = value || '';
    nextTick(resize);
  }
);

onBeforeUnmount(() => clearTimeout(draftTimer));

defineExpose({ focus: () => textareaRef.value?.focus() });
</script>

<template>
  <div class="relative px-4 pb-4">
    <ul
      v-if="suggestions.length"
      class="absolute bottom-full left-4 z-20 mb-1 w-64 p-1 rounded-lg bg-n-solid-2 shadow-md outline outline-1 outline-n-weak"
    >
      <li
        v-for="(person, index) in suggestions"
        :key="person.id"
        class="px-2 py-1.5 rounded-md text-sm cursor-pointer text-n-slate-12"
        :class="{ 'bg-n-alpha-2': index === mentionIndex }"
        @mousedown.prevent="pickMention(person)"
      >
        {{ person.name }}
      </li>
    </ul>

    <div
      class="flex flex-col gap-2 p-2 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 focus-within:outline-n-brand"
    >
      <div v-if="files.length" class="flex flex-wrap gap-1">
        <span
          v-for="(file, index) in files"
          :key="`${file.name}-${index}`"
          class="flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-n-alpha-2 text-n-slate-12"
        >
          {{ file.name }}
          <button
            type="button"
            class="text-n-slate-10"
            @click="removeFile(index)"
          >
            <Icon icon="i-lucide-x" class="size-3" />
          </button>
        </span>
      </div>
      <textarea
        ref="textareaRef"
        v-model="text"
        rows="1"
        class="!mb-0 !border-0 !p-1 !shadow-none resize-none text-sm bg-transparent focus:!ring-0"
        :placeholder="placeholder"
        @input="onInput"
        @keydown="onKeydown"
        @click="updateMentionQuery"
      />
      <div class="flex items-center justify-between">
        <button
          type="button"
          class="size-7 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('LOCO.CHAT.ATTACH')"
          @click="fileInputRef.click()"
        >
          <Icon icon="i-lucide-paperclip" class="size-4" />
        </button>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="hidden"
          @change="onFiles"
        />
        <Button
          xs
          icon="i-lucide-send-horizontal"
          :label="t('LOCO.CHAT.SEND')"
          :disabled="!canSend"
          :is-loading="isSending"
          @click="send"
        />
      </div>
    </div>
  </div>
</template>
